/**
 * Single source of runtime configuration.
 *
 * Nothing outside this file should read `import.meta.env` or hardcode a host.
 * When the Python backend comes online, pointing the frontend at it is a
 * one-line change here plus a `.env` value — no component edits.
 *
 * Vite only exposes env vars prefixed `VITE_`. See `.env.example`.
 *
 * NOTE for anyone doing dead-code cleanup: `API_BASE_URL` and `apiUrl()` have no
 * caller yet and an unused-export scan will flag both. Keep them. They are the
 * single configuration point §11 of the brief requires, and `apiUrl()` is what
 * each mock body in src/api/ becomes a `fetch` against once the Python backend
 * exists. Deleting them would push host knowledge back into the modules.
 *
 * NO SECRETS LIVE HERE. Vite inlines every VITE_ value into the JavaScript it
 * serves, so anything read below is public by construction. The OAuth section at
 * the bottom of this file says which half of a provider's credentials that means
 * we can hold and which half has to stay on the server.
 */
import type { AuthProviderConfig, CheckoutProductId } from './types/domain';

/**
 * Base URL for the backend HTTP/JSON API, without a trailing slash.
 *
 * Runtime no longer falls back to client-owned fixtures when this is empty: the app now
 * treats a missing base URL as a configuration error and lets the real request path fail
 * loudly. Only the Vitest environment keeps the fixture-backed seams alive.
 */
const RAW_API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/** True in Vitest, where the runtime API seams stay fixture-backed. */
export const IS_TEST_ENV: boolean = import.meta.env.MODE === 'test';

/** True in the Vite dev server, where developer-only UI seeds are allowed. */
export const IS_DEV_ENV: boolean = import.meta.env.MODE === 'development';

/*
  Tests run against the mock seams by default, even though this repo's .env points local dev
  at /api. The explicit backend-mode tests stub a different value and reset modules, which this
  preserves; the default '/api' from .env is treated as dev wiring, not as a real backend
  contract under Vitest.
*/
export const API_BASE_URL: string =
  IS_TEST_ENV && (RAW_API_BASE_URL === '' || RAW_API_BASE_URL === '/api')
    ? ''
    : RAW_API_BASE_URL.replace(/\/+$/, '');

/**
 * True only for the fixture-backed test path.
 *
 * Runtime always takes the backend code path now, even when misconfigured, so the failure is
 * explicit instead of silently serving local mock data. Tests still default to fixtures, while
 * backend-mode tests can opt into the real request path by stubbing a non-empty base URL.
 */
export const USE_MOCK_API: boolean = IS_TEST_ENV && API_BASE_URL === '';

/** Public Cloudflare Turnstile widget key. The matching secret stays on the server. */
export const TURNSTILE_SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

function flagEnabled(value: string | undefined): boolean {
  return value === '1' || value?.toLowerCase() === 'true' || value?.toLowerCase() === 'yes';
}

/** Enables additive demo creator/stat baselines for discoverable admin songs. */
export const LYRIXA_DEMO_STATS_ENABLED: boolean = flagEnabled(import.meta.env.VITE_LYRIXA_DEMO_STATS);

/**
 * Builds a full API URL from a path.
 *
 * Throws when no backend is configured, which is deliberate: runtime mock mode is gone, so a
 * real request reaching this point with no base URL is a wiring/configuration fault that should
 * surface immediately.
 */
export function apiUrl(path: string): string {
  if (!API_BASE_URL) {
    throw new Error(
      `No VITE_API_BASE_URL configured — cannot build a URL for "${path}". ` +
        'Runtime mock mode has been removed; configure the backend URL to run the app.'
    );
  }
  return `${API_BASE_URL}/${path.replace(/^\/+/, '')}`;
}

/** Artificial latency (ms) applied by the mock API so loading states are exercised. */
export const MOCK_LATENCY_MS = 350;

/* ===========================================================================
   THIRD-PARTY SIGN-IN — Google and Apple
   ===========================================================================
   WHAT IS HERE AND WHAT IS DELIBERATELY NOT.

   A client id is PUBLIC. It travels in the query string of the authorize URL the
   browser is redirected to, so shipping it in the bundle gives nothing away, and
   reading it here rather than in the auth screen keeps the one rule this file
   states — nothing outside it touches import.meta.env.

   A client SECRET is not public, and there is none in this file, in `.env.example`,
   or anywhere else under src/. It is only needed for the authorization-code
   exchange, which happens on the backend: the browser receives a `code`, posts it
   to our own server, and the server — holding the secret — swaps it for tokens.
   Putting the secret in a VITE_ variable would publish it to every visitor, since
   Vite inlines those values into the JavaScript it serves.

   Apple additionally signs its client assertion with a private key (.p8). That key
   is a server-side artefact for the same reason and has no frontend counterpart.

   Both values below are EMPTY in this build, so `OAUTH_PROVIDERS` reports both
   providers as unconfigured and the sign-in buttons say so instead of starting a
   flow that cannot finish. See src/api/authApi.ts, `signInWithProvider`.
*/

/** Public Google OAuth client id, or '' when unset. */
export const GOOGLE_CLIENT_ID: string = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

/** Public Apple Sign in with Apple services id, or '' when unset. */
export const APPLE_CLIENT_ID: string = import.meta.env.VITE_APPLE_CLIENT_ID ?? '';

/** Public LiveChat widget identifiers. The group keeps this product's chats segregated. */
export const LIVECHAT_LICENSE_ID: string = import.meta.env.VITE_LIVECHAT_LICENSE_ID ?? '';
export const LIVECHAT_GROUP_ID: string = import.meta.env.VITE_LIVECHAT_GROUP_ID ?? '';

/**
 * The two providers the auth screen offers, with whether each is usable.
 *
 * Derived rather than hand-maintained, so a configured provider becomes a working
 * button by setting one env var and nothing else. Ordered Google then Apple, which
 * is the order the buttons render in.
 */
export const OAUTH_PROVIDERS: readonly AuthProviderConfig[] = [
  {
    id: 'google',
    label: 'Google',
    clientId: GOOGLE_CLIENT_ID,
    configured: GOOGLE_CLIENT_ID !== '',
  },
  {
    id: 'apple',
    label: 'Apple',
    clientId: APPLE_CLIENT_ID,
    configured: APPLE_CLIENT_ID !== '',
  },
];

export const JVZOO_PLACEHOLDER_BASE_URL: string =
  import.meta.env.VITE_JVZOO_PLACEHOLDER_BASE_URL ?? 'https://www.jvzoo.com';

export const JVZOO_PRODUCT_URLS: Readonly<Record<CheckoutProductId, string>> = {
  frontend:
    import.meta.env.VITE_JVZOO_FRONTEND_URL ?? 'https://www.jvzoo.com/b/118235/452965/6',
  reseller:
    import.meta.env.VITE_JVZOO_RESELLER_URL ?? 'https://www.jvzoo.com/b/118235/452997/6',
  'complete-creator-bundle':
    import.meta.env.VITE_JVZOO_COMPLETE_CREATOR_BUNDLE_URL ??
    'https://www.jvzoo.com/b/118235/452989/6',
  fastpass:
    import.meta.env.VITE_JVZOO_FASTPASS_URL ?? 'https://www.jvzoo.com/b/118235/452987/6',
  professional:
    import.meta.env.VITE_JVZOO_PROFESSIONAL_URL ?? 'https://www.jvzoo.com/b/118235/452985/6',
  'publisher-monetization':
    import.meta.env.VITE_JVZOO_PUBLISHER_MONETIZATION_URL ??
    'https://www.jvzoo.com/b/118235/452983/6',
  'music-dna-composer':
    import.meta.env.VITE_JVZOO_MUSIC_DNA_COMPOSER_URL ??
    'https://www.jvzoo.com/b/118235/452979/6',
  'studio-card':
    import.meta.env.VITE_JVZOO_STUDIO_CARD_URL ?? 'https://www.jvzoo.com/b/118235/452975/6',
  'unlimited-creation-publishing':
    import.meta.env.VITE_JVZOO_UNLIMITED_CREATION_PUBLISHING_URL ??
    'https://www.jvzoo.com/b/118235/452967/6',
  'commercial-certification':
    import.meta.env.VITE_JVZOO_COMMERCIAL_CERTIFICATION_URL ??
    'https://www.jvzoo.com/b/118235/451433/6',
  'dfy-producer-vault':
    import.meta.env.VITE_JVZOO_DFY_PRODUCER_VAULT_URL ??
    'https://www.jvzoo.com/b/118235/452971/6',
};

export const JVZOO_TOPUP_URLS: Readonly<Record<string, string>> = {
  p50: import.meta.env.VITE_JVZOO_PACK50_URL ?? 'https://www.jvzoo.com/b/118237/455301/1',
  p200: import.meta.env.VITE_JVZOO_PACK200_URL ?? 'https://www.jvzoo.com/b/118237/455309/1',
  p1000: import.meta.env.VITE_JVZOO_PACK1000_URL ?? 'https://www.jvzoo.com/b/118237/455311/1',
};

export const JVZOO_TOPUP_IMAGES: Readonly<Record<string, string>> = {
  p50: 'https://i.jvzoo.com/118237/455301/1',
  p200: 'https://i.jvzoo.com/118237/455309/1',
  p1000: 'https://i.jvzoo.com/118237/455311/1',
};

export const JVZOO_PUBLISHER_CREDIT_URLS: Readonly<Record<string, string>> = {
  pub5:
    import.meta.env.VITE_JVZOO_PUBLISHER_CREDITS_5_URL ??
    `${JVZOO_PLACEHOLDER_BASE_URL}/publisher-credits-5`,
  pub10:
    import.meta.env.VITE_JVZOO_PUBLISHER_CREDITS_10_URL ??
    `${JVZOO_PLACEHOLDER_BASE_URL}/publisher-credits-10`,
  pub25:
    import.meta.env.VITE_JVZOO_PUBLISHER_CREDITS_25_URL ??
    `${JVZOO_PLACEHOLDER_BASE_URL}/publisher-credits-25`,
  pub50:
    import.meta.env.VITE_JVZOO_PUBLISHER_CREDITS_50_URL ??
    `${JVZOO_PLACEHOLDER_BASE_URL}/publisher-credits-50`,
};
